import { apiRequest, buildApiUrl, API_CONFIG } from '../utils/api';

// Servicio para manejar el dashboard
export class DashboardService {
  // Obtener datos del dashboard
  static async getDashboardData() {
    try {
      const response = await apiRequest(buildApiUrl(API_CONFIG.ENDPOINTS.DASHBOARD), {
        method: 'GET'
      });
      
      console.log('✅ Respuesta del dashboard:', response);
      
      if (response.response && response.response.content && response.response.content.dashboard) {
        return {
          success: true,
          data: response.response.content.dashboard,
          operationId: response.operationId
        };
      } else {
        throw new Error('No dashboard data found in response');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      return {
        success: false,
        error: error.message,
        data: null
      };
    }
  }

  static async getAccountInfo() {
    try {
      const response = await apiRequest(buildApiUrl(API_CONFIG.ENDPOINTS.ACCOUNT), {
        method: 'GET'
      });

      console.log('✅ Respuesta de cuenta:', response);

      const accountList = response.response?.content?.account || [];

      return {
        success: true,
        account: accountList,
        operationId: response.operationId
      };
    } catch (error) {
      console.error('Error fetching account info:', error);
      return {
        success: false,
        error: error.message,
        account: []
      };
    }
  }
}

// Función helper para formatear fechas ISO
const formatISODate = (isoDate) => {
  if (!isoDate) return 'Sin fecha';
  const date = new Date(isoDate);
  return date.toLocaleDateString('es-ES', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
};

// Función helper para formatear fechas ISO con hora
const formatISODateTime = (isoDate) => {
  if (!isoDate) return 'Sin fecha';
  const date = new Date(isoDate);
  return date.toLocaleString('es-ES', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Función helper para obtener color según el estado
const getProjectColor = (status) => {
  const colorMap = {
    'pending': 'blue',
    'in-progress': 'green',
    'completed': 'success',
    'backlog': 'gray',
    'cancelled': 'red'
  };
  return colorMap[status] || 'blue';
};

// Función helper para obtener icono según el nombre
const getProjectIcon = (name) => {
  const iconMap = {
    'api': '🔌',
    'login': '🔐',
    'onboarding': '🚀',
    'testing': '🧪',
    'qa': '🧪',
    'mobile': '📱',
    'web': '🌐',
    'backend': '⚙️',
    'frontend': '🎨',
    'tec': '💻'
  };
  
  const lowerName = name.toLowerCase();
  for (const [key, icon] of Object.entries(iconMap)) {
    if (lowerName.includes(key)) {
      return icon;
    }
  }
  
  return '📋'; // Icono por defecto
};

// Función helper para formatear los datos del dashboard
export const formatDashboardData = (dashboardData) => {
  if (!dashboardData) {
    console.error('❌ formatDashboardData recibió datos nulos o indefinidos');
    return null;
  }
  
  return {
    activeProjects: dashboardData.activeProjects || 0,
    activeTasks: dashboardData.activeTasks || 0,
    totalProjects: dashboardData.totalProjects || 0,
    progress: dashboardData.progress || 0,
    recentEvents: (dashboardData.recentEvents || []).map(event => ({
      id: `${event.title}-${event.date}`,
      title: event.title || 'Sin título',
      description: event.description || 'Sin descripción',
      date: formatISODate(event.date),
      dateTime: formatISODateTime(event.date),
      rawDate: event.date
    })),
    upcomingDates: (dashboardData.upcomingDates || []).map(event => ({
      id: event.id?.toString() || `${event.name}-${event.initial_date}`,
      name: event.name || 'Sin nombre',
      description: event.description || 'Sin descripción',
      status: event.status || 'pending',
      initialDate: formatISODate(event.initial_date),
      initialDateTime: formatISODateTime(event.initial_date),
      finalDate: event.final_date ? formatISODate(event.final_date) : null,
      finalDateTime: event.final_date ? formatISODateTime(event.final_date) : null,
      rawInitialDate: event.initial_date,
      rawFinalDate: event.final_date,
      participants: event.participants || '',
      corporationId: event.corporation_id
    })),
    recentProjects: (dashboardData.recentProjects || []).map(project => ({
      id: project.id?.toString() || String(Date.now()),
      name: project.name || 'Sin nombre',
      description: project.description || 'Sin descripción',
      status: project.status || 'pending',
      initialDate: project.initial_date,
      finalDate: project.final_date,
      corporationId: project.corporation_id,
      progress: project.progress || 0,
      tasksCount: project.tasks || 0,
      color: getProjectColor(project.status || 'pending'),
      icon: getProjectIcon(project.name || ''),
      methodology: 'scrum' // Por defecto, se puede ajustar según la API
    }))
  };
};

